import { App as CapApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Opens Stripe Checkout in the system in-app browser and resolves once we know
 * how it ended. The deep link back from the success page is layer 1 and is
 * handled by AppUrlListener; this poller is the safety net for when it doesn't
 * fire (user taps Done mid-checkout, universal link swallowed by the redirect).
 *
 * - iOS: closing SFSafariViewController fires `browserFinished` (the app never
 *   backgrounds, so `appStateChange` can't be relied on there).
 * - Android: closing the custom tab foregrounds the app → `appStateChange`
 *   (`browserFinished` support varies by browser).
 */
export const openNativeCheckout = async (
  url: string,
  checkPaid: () => Promise<boolean>
): Promise<"paid" | "unpaid"> => {
  await Browser.open({ url });

  return new Promise((resolve) => {
    let settled = false;
    let handles: Array<{ remove: () => Promise<void> }> = [];

    const settle = (result: "paid" | "unpaid") => {
      if (settled) return;
      settled = true;
      handles.forEach((h) => h.remove().catch(() => {}));
      resolve(result);
    };

    // Webhook latency means "not paid yet" can turn into "paid" a moment later.
    const pollAndSettle = async () => {
      for (let attempt = 0; attempt < 5 && !settled; attempt++) {
        try {
          if (await checkPaid()) {
            settle("paid");
            return;
          }
        } catch {
          // API hiccup — keep polling
        }
        await wait(2000);
      }
      settle("unpaid");
    };

    Promise.all([
      Browser.addListener("browserFinished", pollAndSettle),
      CapApp.addListener("appStateChange", ({ isActive }) => {
        if (isActive) pollAndSettle();
      }),
    ]).then((registered) => {
      handles = registered;
      if (settled) registered.forEach((h) => h.remove().catch(() => {}));
    });
  });
};
